import React, { useMemo } from 'react';
import InfoTooltip from './info-tooltip';

const pickI18n = (obj, lang, fallbacks = ['fr', 'en', 'de']) =>
    obj?.[lang] || fallbacks.map(l => obj?.[l]).find(Boolean) || '';

export default function FieldInput({ def, lang, value, error, onChange }) {
    const {
        key, type = 'text', required,
        label, labels, description, descriptions, placeholder, placeholders,
        options = []
    } = def;

    const name = `fld_${key}`;
    const helpId = `help-${name}`;
    const labelTxt = pickI18n(labels || label || {}, lang);
    const ph = pickI18n(placeholders || placeholder || {}, lang);
    const help = pickI18n(descriptions || description || {}, lang);

    const selectOptions = useMemo(() => (Array.isArray(options) ? options : []), [options]);

    const baseInputClass =
        `w-full p-3 text-sm text-secondary bg-white border ${error ? "border-error" : "border-borderColor"} rounded-md transition`;

    const baseProps = {
        id: name,
        name,
        placeholder: ph || undefined,
        'aria-invalid': !!error,
        ...(help ? { 'aria-describedby': helpId } : {}),
        className: baseInputClass,
    };

    return (
        <div className="flex flex-col">
            {/* Label row with info tooltip */}
            <div className="flex items-center mb-1">
                <label htmlFor={name} className="block text-sm font-medium text-secondary">
                    {labelTxt}{required && <span className="text-red-500"> *</span>}
                </label>
                {help && (
                    <InfoTooltip text={help} describedById={helpId} />
                )}
            </div>

            {/* Hidden help for screen readers (tooltip is visual only) */}
            {help && (
                <span id={helpId} className="sr-only">
                    {help}
                </span>
            )}

            {/* inputs */}
            {type === 'textarea' && (
                <textarea
                    rows={3}
                    {...baseProps}
                    value={value ?? ''}
                    onChange={(e) => onChange(name, e.target.value)}
                />
            )}

            {(type === 'text' || type === 'email' || type === 'tel' || type === 'date' || type === 'number') && (
                <input
                    type={type === 'number' ? 'number' : type}
                    {...baseProps}
                    value={value ?? ''}
                    onChange={(e) => {
                        const v = type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value;
                        onChange(name, v);
                    }}
                />
            )}

            {type === 'select' && (
                <div className="relative">
                    <select
                        {...baseProps}
                        value={value ?? ''}
                        onChange={(e) => onChange(name, e.target.value)}
                        className={`${baseInputClass} appearance-none pr-10`}
                    >
                        <option value="" disabled>{ph || '—'}</option>
                        {selectOptions.map((opt) => (
                            <option key={String(opt)} value={opt}>{opt}</option>
                        ))}
                    </select>
                    {/* caret */}
                    <svg
                        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"
                        viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M10 12a1 1 0 0 1-.707-.293l-4-4a1 1 0 1 1 1.414-1.414L10 9.586l3.293-3.293a1 1 0 1 1 1.414 1.414l-4 4A1 1 0 0 1 10 12z" clipRule="evenodd" />
                    </svg>
                </div>
            )}

            {type === 'checkbox' && (
                <label className="inline-flex items-center gap-2">
                    <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={!!value}
                        onChange={(e) => onChange(name, e.target.checked)}
                        aria-describedby={help ? helpId : undefined}
                    />
                    <span className="text-sm text-secondary">
                        {ph}
                    </span>
                    {help && <InfoTooltip text={help} describedById={helpId} />}
                </label>
            )}

            {type === 'file' && (
                <input
                    type="file"
                    className={`block w-full text-sm 
                      file:mr-4 file:py-2.5 file:px-4 
                      file:rounded-lg file:border-0 file:font-semibold
                      file:bg-primary file:text-white file:hover:bg-primary/90
                      ${error ? 'border-red-300' : 'border-gray-300'}`}
                    onChange={(e) => onChange(name, e.target.files?.[0] || null)}
                    aria-describedby={help ? helpId : undefined}
                />
            )}

            {/* Remove the old inline help text; errors remain below input */}
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        </div>
    );
}